import { BASE_URL } from './Constants';
import { END_POINTS } from './service/EndPoints';

const ACTIVE_PREFIX = 'patient_call_active_';
const LEFT_PREFIX = 'patient_call_left_';
const CLOSED_PREFIX = 'patient_call_closed_';
const SESSION_ENDED_MSG_KEY = 'patient_session_ended_msg';
const FEEDBACK_ROOM_KEY = 'patient_feedback_room_id';

/** Prevent double pagehide+beforeunload beacons on the same room */
const _recentBeaconAt = {};
const BEACON_DEDUP_MS = 4000;

const activeKey = (roomId) => `${ACTIVE_PREFIX}${roomId}`;
const leftKey = (roomId) => `${LEFT_PREFIX}${roomId}`;
const closedKey = (roomId) => `${CLOSED_PREFIX}${roomId}`;

export const isPageReload = () => {
    try {
        const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        if (nav && nav.type === 'reload') {
            return true;
        }
    } catch (e) {
        /* ignore */
    }
    try {
        // Legacy API
        return typeof performance !== 'undefined'
            && performance.navigation
            && performance.navigation.type === 1;
    } catch (e) {
        return false;
    }
};

const PatientCallSession = {
    markActive(roomId) {
        if (!roomId) return;
        try {
            sessionStorage.setItem(activeKey(roomId), '1');
            sessionStorage.removeItem(leftKey(roomId));
            sessionStorage.removeItem(closedKey(roomId));
            // New live call — never show a leftover "session ended" toast
            sessionStorage.removeItem(SESSION_ENDED_MSG_KEY);
        } catch (e) {
            /* ignore */
        }
    },

    clearActive(roomId) {
        if (!roomId) return;
        try {
            sessionStorage.removeItem(activeKey(roomId));
        } catch (e) {
            /* ignore */
        }
    },

    isActive(roomId) {
        if (!roomId) return false;
        try {
            return sessionStorage.getItem(activeKey(roomId)) === '1';
        } catch (e) {
            return false;
        }
    },

    markLeftMidCall(roomId) {
        if (!roomId) return;
        try {
            sessionStorage.setItem(leftKey(roomId), '1');
        } catch (e) {
            /* ignore */
        }
    },

    clearLeftMidCall(roomId) {
        if (!roomId) return;
        try {
            sessionStorage.removeItem(leftKey(roomId));
        } catch (e) {
            /* ignore */
        }
    },

    leftMidCall(roomId) {
        if (!roomId) return false;
        try {
            return sessionStorage.getItem(leftKey(roomId)) === '1';
        } catch (e) {
            return false;
        }
    },

    markClosed(roomId) {
        if (!roomId) return;
        try {
            sessionStorage.setItem(closedKey(roomId), '1');
            sessionStorage.removeItem(activeKey(roomId));
            sessionStorage.removeItem(leftKey(roomId));
        } catch (e) {
            /* ignore */
        }
    },

    clearClosed(roomId) {
        if (!roomId) return;
        try {
            sessionStorage.removeItem(closedKey(roomId));
        } catch (e) {
            /* ignore */
        }
    },

    isClosed(roomId) {
        if (!roomId) return false;
        try {
            return sessionStorage.getItem(closedKey(roomId)) === '1';
        } catch (e) {
            return false;
        }
    },

    /** Refresh / remount after leaving mid-call / closed session */
    shouldTreatAsSessionEnded(roomId) {
        if (!roomId) return false;
        if (PatientCallSession.isClosed(roomId)) {
            return true;
        }
        if (PatientCallSession.leftMidCall(roomId)) {
            return true;
        }
        if (isPageReload() && PatientCallSession.isActive(roomId)) {
            return true;
        }
        return false;
    },

    flagSessionEndedMessage() {
        try {
            sessionStorage.setItem(SESSION_ENDED_MSG_KEY, '1');
        } catch (e) {
            /* ignore */
        }
    },

    clearSessionEndedMessage() {
        try {
            sessionStorage.removeItem(SESSION_ENDED_MSG_KEY);
        } catch (e) {
            /* ignore */
        }
    },

    consumeSessionEndedMessage() {
        try {
            const msg = sessionStorage.getItem(SESSION_ENDED_MSG_KEY) === '1';
            if (msg) {
                sessionStorage.removeItem(SESSION_ENDED_MSG_KEY);
            }
            return msg;
        } catch (e) {
            return false;
        }
    },

    /** Survive Feedback hard-refresh (Redux roomId is lost) */
    saveFeedbackRoomId(roomId) {
        if (!roomId) return;
        try {
            sessionStorage.setItem(FEEDBACK_ROOM_KEY, String(roomId));
        } catch (e) {
            /* ignore */
        }
    },

    getFeedbackRoomId() {
        try {
            return sessionStorage.getItem(FEEDBACK_ROOM_KEY) || null;
        } catch (e) {
            return null;
        }
    },

    clearFeedbackRoomId() {
        try {
            sessionStorage.removeItem(FEEDBACK_ROOM_KEY);
        } catch (e) {
            /* ignore */
        }
    },

    /**
     * Best-effort end call while the tab is unloading (mobile refresh / close).
     * Uses fetch keepalive so the request can outlive the page.
     * Dedupes pagehide+beforeunload double-fire on mobile browsers.
     */
    beaconEndCall({ roomId, srm_ip_room }) {
        if (!roomId) return;
        const now = Date.now();
        if (_recentBeaconAt[roomId] && (now - _recentBeaconAt[roomId]) < BEACON_DEDUP_MS) {
            return;
        }
        _recentBeaconAt[roomId] = now;
        const base = (BASE_URL || '').replace(/\/?$/, '/');
        const url = `${base}${END_POINTS.CUSTOMER_DECLINE_API}`;
        // Truthy srm_ip_room is required so API updates SrmIpAppointment (not regular Appointment).
        let srmFlag = srm_ip_room;
        if (!srmFlag && typeof localStorage !== 'undefined') {
            srmFlag = localStorage.getItem('srm_ip_room')
                || localStorage.getItem('srm_ip_encrypt_room_id');
        }
        if (!srmFlag && typeof window !== 'undefined' && window.location) {
            const path = window.location.pathname || '';
            if (/\/SRM_IP\//i.test(path) || /\/srm_head\//i.test(path) || /\/srm_ward\//i.test(path)) {
                srmFlag = roomId || '1';
            }
        }
        const body = JSON.stringify({
            roomId,
            callEndTime: Date.now(),
            srm_ip_room: srmFlag || null,
            // Tab close while ringing — treat as patient decline (missed)
            reason: 'patient_declined',
        });
        try {
            if (typeof fetch === 'function') {
                fetch(url, {
                    method: 'POST',
                    headers: {
                        Authorization: 'Basic YWdlbnQ6YWdlbnQ=',
                        'Content-Type': 'application/json',
                    },
                    body,
                    keepalive: true,
                }).catch(() => { /* ignore */ });
                return;
            }
        } catch (e) {
            /* fall through */
        }
        try {
            if (navigator.sendBeacon) {
                const blob = new Blob([body], { type: 'application/json' });
                navigator.sendBeacon(url, blob);
            }
        } catch (e) {
            /* ignore */
        }
    },
};

export default PatientCallSession;
